import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

export type ContractRole = 'provider' | 'client';

const ROLES: ReadonlyArray<{ value: ContractRole; labelKey: string; defaultLabel: string }> = [
  {
    value: 'provider',
    labelKey: 'ORGCONTRACTCHECKER_ROLE_PROVIDER',
    defaultLabel: 'I am the Service Provider',
  },
  {
    value: 'client',
    labelKey: 'ORGCONTRACTCHECKER_ROLE_CLIENT',
    defaultLabel: 'I am the Client',
  },
];

interface RoleSelectorProps {
  value: ContractRole | null;
  onChange: (next: ContractRole) => void;
}

/**
 * Radio-style role picker — "I am the Service Provider" / "I am the
 * Client". Rendered as buttons (rather than `<input type="radio">`)
 * so the entire row is the hit target and styling is fully under our
 * control. `aria-pressed` keeps the toggle state announced to AT.
 */
export function RoleSelector({ value, onChange }: RoleSelectorProps) {
  const { t } = useTranslation();

  return (
    <fieldset>
      <legend className="block mb-2 text-[11px] font-semibold uppercase tracking-wider text-globe-grey">
        {t('ORGCONTRACTCHECKER_LABEL_ROLE', {
          defaultValue: 'What is your role in this contract?',
        })}
      </legend>
      <div className="space-y-3">
        {ROLES.map((role) => {
          const selected = value === role.value;
          return (
            <button
              key={role.value}
              type="button"
              onClick={() => onChange(role.value)}
              aria-pressed={selected}
              className={cn(
                'w-full text-left p-3 sm:p-4 rounded-lg border transition-all duration-150 flex items-center gap-3',
                selected
                  ? 'border-selise-blue bg-selise-blue/5'
                  : 'border-border hover:bg-muted/50'
              )}
            >
              <span
                className={cn(
                  'w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0',
                  selected ? 'border-selise-blue' : 'border-border'
                )}
                aria-hidden
              >
                {selected && <span className="w-2.5 h-2.5 rounded-full bg-selise-blue" />}
              </span>
              <span className="text-sm font-semibold text-[hsl(var(--fg))]">
                {t(role.labelKey, { defaultValue: role.defaultLabel })}
              </span>
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

export default RoleSelector;
